import { useApolloClient } from '@apollo/client'
import { Dispatch, SetStateAction, useCallback } from 'react'
import { DetailedTableRow } from '~/components/tables/types'

/**
 * Local bookkeeping for the row-action "processing" spinner (the row checkbox is replaced by a
 * spinner while `processingWorkspaceButton` is set), writing both the Apollo cache and the
 * `rowsData` state the grid renders from.
 *
 * The pushRowButtonNodes subscription is the single source of truth for clearing this state once
 * an action ran: a fast-completing action can publish its clear event before the mutation
 * response reaches the client, so re-asserting the processing state from the mutation response
 * would re-arm the spinner permanently (INC-1970). Callers mark rows as processing when the
 * action is triggered, and afterwards only ever clear rows the server did not execute.
 */
export const useRowProcessingState = (
  setRowsData: Dispatch<SetStateAction<DetailedTableRow[]>>,
) => {
  const apolloClient = useApolloClient()

  const writeProcessingState = useCallback(
    (rowIds: string[], button: { id: string; name: string } | null) => {
      const processingWorkspaceButton = button
        ? { __typename: 'WorkspaceButton' as const, id: button.id, name: button.name }
        : null

      rowIds.forEach((rowId) => {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({ __typename: 'TableRow', id: rowId }),
          fields: {
            processingWorkspaceButton() {
              return processingWorkspaceButton
            },
          },
        })
      })

      const rowIdSet = new Set(rowIds)
      setRowsData((prev) =>
        prev.map((row) => (rowIdSet.has(row.id) ? { ...row, processingWorkspaceButton } : row)),
      )
    },
    [apolloClient, setRowsData],
  )

  const markRowsProcessing = useCallback(
    (rowIds: string[], button: { id: string; name: string }) => {
      writeProcessingState(rowIds, button)
    },
    [writeProcessingState],
  )

  const clearRowsProcessing = useCallback(
    (rowIds: string[]) => {
      if (rowIds.length > 0) {
        writeProcessingState(rowIds, null)
      }
    },
    [writeProcessingState],
  )

  return { markRowsProcessing, clearRowsProcessing }
}
